import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import uniq from 'lodash/uniq';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, scan, startWith, switchMap } from 'rxjs/operators';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { SessionModule } from 'app/src/sessions/session.module';
import { PipesModule } from 'app/src/shared/pipes.module';

import { CoreApiService } from '../../core-api.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { arrayLengthMin } from '../../shared/form.utils';
import { SearchField } from '../../shared/models/search/search-field.model';
import { SearchQuery } from '../../shared/models/search/search-query.model';

export interface TrainingSetPayload {
  file_count: number;
  files?: {
    _id: string;
  }[];
  search_query?: SearchQuery;
}

@Component({
  templateUrl: './training-set-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    PipesModule,
    ReactiveFormsModule,
    MaterialModule,
    SessionModule,
  ],
})
export class TrainingSetDialog {
  payload = inject<Partial<TrainingSetPayload>>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<TrainingSetDialog>>(MatDialogRef);
  private coreApi = inject(CoreApiService);
  private notifications = inject(NotificationService);
  private projectsService = inject(ProjectsService);

  public form: FormGroup;
  public fields$: Observable<SearchField[]>;
  public outputFilePath$: Observable<string>;
  public labelValueStream$ = new Subject<string>();
  public groups$ = this.projectsService.getGroupedProjects([PermissionAction.FilesCreateSingle]);

  @ViewChild('labelChipInput', { static: false }) labelChipInput: ElementRef;

  private projects$ = this.projectsService.getProjects();
  private defaultFilename = `training_set_${new Date().toISOString().replace(/:/g, '.')}.json`;

  constructor() {
    this.form = new FormGroup({
      projectId: new FormControl(null, Validators.required),
      labels: new FormControl([], arrayLengthMin(1)),
      labelInput: new FormControl(''),
      filename: new FormControl(this.defaultFilename),
    });

    this.fields$ = this.form.controls.labelInput.valueChanges.pipe(
      startWith(this.form.controls.labelInput.value),
      filter((data) => Boolean(data)),
      switchMap((value) => this.coreApi.dataexplorer.fields({ field: value })),
    );

    this.outputFilePath$ = combineLatest(
      this.form.controls.projectId.valueChanges,
      this.form.controls.filename.valueChanges.pipe(startWith(this.defaultFilename)),
      this.projects$,
    ).pipe(
      map(([projectId, filename, allProjects]) => {
        const project = allProjects.find((project) => project._id === projectId);
        return project ? `${project.label} / ${filename || this.defaultFilename}` : '';
      }),
    );

    /**
     * Handle chip suggestion selection and offroad enter/blur values in one stream.
     * The events for the chip autocomplete and its input stomp on one another in an
     * inconsistent order, so we pick the longest value in the stream over a short
     * period, add it to the chip array, then reset the stream with an empty value.
     */
    this.labelValueStream$
      .pipe(
        scan((specific, value) => {
          this.form.controls.labels.setErrors(null); // pause validation
          if (value) {
            return value.length > specific.length ? value : specific;
          }
          return value;
        }),
        debounceTime(200),
        filter((value) => value !== ''),
      )
      .subscribe((value) => {
        const labels = this.form.controls.labels;
        labels.setValue(uniq([...labels.value, value]));
        this.labelChipInput.nativeElement.value = '';
        this.labelValueStream$.next(''); // reset the stream
      });
  }

  removeChip(index: number): void {
    const labels = this.form.controls.labels;
    labels.setValue([...labels.value.slice(0, index), ...labels.value.slice(index + 1)]);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onCreate(): void {
    if (!this.form.valid) {
      return;
    }
    const payload = {
      ...this.payload,
      labels: this.form.get('labels').value,
      output: {
        type: 'project',
        id: this.form.get('projectId').value,
      },
      filename: this.form.get('filename').value || this.defaultFilename,
    };
    this.form.disable();
    this.coreApi.dataexplorer.training(payload).subscribe({
      next: () => {
        this.notifications.success('dataexplorer.saveTrainingSet.success');
        this.dialogRef.close();
      },
      error: (error) => {
        console.log(error); // eslint-disable-line no-console
        this.notifications.error('dataexplorer.saveTrainingSet.error');
        this.form.enable();
      },
    });
  }
}
